import { Stack, Typography } from '@ace-clublabs/shared-component-library';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ProgressBar from '@/components/shared/ProgressBar';
import type { Branch } from '@/features/appointments/utils/Branch';
import LocationSearchBox from '@/features/scrollFlow/pages/SelectAgent/components/LocationSearchBox';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useProgress from '@/hooks/useProgress';
import useResolvedZipCode from '@/hooks/useResolvedZipCode';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import { selectTrvEndLocationForTagging } from '@/lib/tagging/utils';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import { useBranchesQuery } from '../api/getBranches';
import BranchesMap from './BranchesMap';
import BranchSelectionList from './BranchSelectionList';

const BranchSelection: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const tagging = useTagging();
  const tagged = useRef(false);

  useLandingPageRedirection();
  useProgress(30);

  const initialZipCode = useResolvedZipCode();
  const agentSearchLocation = useAppointmentStore(
    (state) => state.agentSearchLocation,
  );

  const searchParams = agentSearchLocation
    ? {
        zipCode: agentSearchLocation.zipCode,
        lat: agentSearchLocation.lat,
        lng: agentSearchLocation.lng,
      }
    : { zipCode: initialZipCode || '' };

  const { data: branches = [], isLoading } = useBranchesQuery(
    searchParams,
    !!searchParams.zipCode || (!!searchParams.lat && !!searchParams.lng),
  );

  const selectedBranch = useAppointmentStore((state) => state.selectedBranch);
  const setSelectedBranch = useAppointmentStore(
    (state) => state.setSelectedBranch,
  );

  const trvEndLocation = useAppointmentStore(selectTrvEndLocationForTagging);
  const { sessionId, sessionSource } = useSessionStore();

  const [localSelectedBranch, setLocalSelectedBranch] = useState<Branch | null>(
    selectedBranch,
  );

  const prevAgentSearchLocationRef = useRef(agentSearchLocation);

  useEffect(() => {
    if (!tagged.current) {
      tagging.tag('view', sessionSource, {
        page: Page.branchSelection,
        sessionid: sessionId,
        flow: '',
        trv_end_location: trvEndLocation,
        comm_pref: 'in person',
      });

      tagged.current = true;
    }
  }, [trvEndLocation, sessionSource, sessionId, tagging.tag]);

  useEffect(() => {
    if (agentSearchLocation !== prevAgentSearchLocationRef.current) {
      setLocalSelectedBranch(null);
      prevAgentSearchLocationRef.current = agentSearchLocation;
    }
  }, [agentSearchLocation]);

  useEffect(() => {
    if (branches.length === 0) return;

    if (!localSelectedBranch) {
      setLocalSelectedBranch(branches[0]);
      return;
    }

    const isStillValid = branches.some(
      (b) => b.branchId === localSelectedBranch.branchId,
    );

    if (!isStillValid) {
      setLocalSelectedBranch(branches[0]);
    }
  }, [branches, localSelectedBranch]);

  const handleClickContinue = () => {
    if (localSelectedBranch) {
      setSelectedBranch(localSelectedBranch);
      navigate(`/schedule-appointment/${id}/date-selection`);
    }
  };

  const isButtonDisabled = isLoading || !localSelectedBranch;

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Stack
        spacing={4}
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-start',
          flex: 1,
          px: 2,
          pt: 4,
          overflow: 'hidden',
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: '100%',
            maxWidth: '600px',
            textAlign: 'left',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant='h5'>
            Find AAA branches near your ZIP code.
          </Typography>

          <LocationSearchBox
            placeholder={
              searchParams.zipCode || agentSearchLocation?.city || ''
            }
            helperText='Search city, or ZIP.'
          />

          <Stack sx={{ flex: 1, minHeight: 0, pb: 1 }}>
            <BranchesMap
              branches={branches}
              selectedBranch={localSelectedBranch}
              onSelectBranch={setLocalSelectedBranch}
            />

            <BranchSelectionList
              branches={branches}
              selectedBranch={localSelectedBranch}
              onSelectBranch={setLocalSelectedBranch}
              isLoading={isLoading}
            />
          </Stack>
        </Stack>
      </Stack>

      <ProgressBar
        buttonDisabled={isButtonDisabled}
        displayBackButton={true}
        onClickContinue={handleClickContinue}
      />
    </Stack>
  );
};

export default BranchSelection;
